import React from 'react';
import {
  Box, Typography, Stack, Checkbox, FormControlLabel, LinearProgress,
  Accordion, AccordionSummary, AccordionDetails, Chip, CircularProgress, TextField, Button,
} from '@mui/material';
import { ExpandMore, FlightTakeoff, Flight, CheckCircle, Add } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { doUpdateChecklist, doAddChecklistItem } from '../../../reduxcomponents/slices/tripManagementSlice';

const phaseConfig = [
  { key: 'before_trip', label: 'Before Trip',  icon: FlightTakeoff, color: 'info' },
  { key: 'during_trip', label: 'During Trip',  icon: Flight,        color: 'success' },
  { key: 'after_trip',  label: 'After Trip',   icon: CheckCircle,   color: 'secondary' },
];

const ChecklistPhase = ({ phase, items = [], tripId, phaseKey }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector(s => s.tripManagement);

  const [newTask, setNewTask] = React.useState('');

  const completed = items.filter(i => i.completed).length;
  const total = items.length;
  const pct = total > 0 ? Math.round((completed / total) * 100) : 0;

  const handleToggle = (label, checked) => {
    dispatch(doUpdateChecklist({ id: tripId, data: { phase: phaseKey, label, completed: checked } }));
  };

  const handleAddTask = async () => {
    if (!newTask.trim()) return;
    try {
      await dispatch(doAddChecklistItem({ id: tripId, data: { phase: phaseKey, label: newTask } })).unwrap();
      setNewTask('');
    } catch (err) {
      console.error(err);
    }
  };

  const Icon = phase.icon;

  return (
    <Accordion defaultExpanded={pct < 100} variant="outlined" sx={{ borderRadius: 1.5, '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Stack direction="row" alignItems="center" spacing={1.5} flex={1} mr={1}>
          <Icon color={phase.color} fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700}>{phase.label}</Typography>
          <Chip
            label={`${completed}/${total}`}
            color={pct === 100 ? 'success' : pct > 50 ? 'warning' : 'error'}
            size="small"
            sx={{ ml: 'auto' }}
          />
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ pt: 0 }}>
        <Box px={1}>
          <LinearProgress
            variant="determinate"
            value={pct}
            color={pct === 100 ? 'success' : pct > 50 ? 'warning' : 'error'}
            sx={{ height: 6, borderRadius: 3, mb: 1.5 }}
          />
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            {pct}% complete
          </Typography>
          <Stack spacing={0.5}>
            {items.map((item) => (
              <FormControlLabel
                key={item.label}
                control={
                  <Checkbox
                    size="small"
                    checked={item.completed}
                    onChange={(e) => handleToggle(item.label, e.target.checked)}
                    disabled={loading.action}
                    color="success"
                  />
                }
                label={
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography
                      variant="body2"
                      sx={{ textDecoration: item.completed ? 'line-through' : 'none', color: item.completed ? 'text.disabled' : 'text.primary' }}
                    >
                      {item.label}
                    </Typography>
                    {item.completed && item.completedAt && (
                      <Typography variant="caption" color="text.disabled">
                        ✓ {new Date(item.completedAt).toLocaleDateString()}
                      </Typography>
                    )}
                  </Stack>
                }
              />
            ))}
          </Stack>
          
          <Stack direction="row" spacing={1} mt={2} mb={1}>
            <TextField
              size="small"
              fullWidth
              placeholder="Add new task..."
              value={newTask}
              onChange={(e) => setNewTask(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddTask(); }}
              disabled={loading.action}
            />
            <Button
              variant="contained"
              size="small"
              onClick={handleAddTask}
              disabled={!newTask.trim() || loading.action}
              sx={{ minWidth: 'auto', px: 2 }}
            >
              <Add fontSize="small" />
            </Button>
          </Stack>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

const TripChecklist = ({ checklist, tripId }) => {
  if (!checklist) {
    checklist = { before_trip: [], during_trip: [], after_trip: [] };
  }

  const allItems = [
    ...Object.values(checklist.before_trip || []),
    ...Object.values(checklist.during_trip || []),
    ...Object.values(checklist.after_trip || []),
  ];
  const totalCompleted = allItems.filter(i => i.completed).length;
  const totalItems = allItems.length;
  const overallPct = totalItems > 0 ? Math.round((totalCompleted / totalItems) * 100) : 0;

  return (
    <Box>
      {/* Overall Progress */}
      <Box bgcolor="grey.50" p={2} borderRadius={2} mb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2" fontWeight={700}>Overall Completion</Typography>
          <Typography variant="h5" fontWeight={700} color={overallPct === 100 ? 'success.main' : 'primary.main'}>
            {overallPct}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={overallPct}
          color={overallPct === 100 ? 'success' : 'primary'}
          sx={{ height: 10, borderRadius: 5 }}
        />
        <Typography variant="caption" color="text.secondary" mt={0.5} display="block">
          {totalCompleted} of {totalItems} items completed
        </Typography>
      </Box>

      {/* Phase Checklists */}
      <Stack spacing={1}>
        {phaseConfig.map(phase => (
          <ChecklistPhase
            key={phase.key}
            phase={phase}
            phaseKey={phase.key}
            items={checklist[phase.key] || []}
            tripId={tripId}
          />
        ))}
      </Stack>
    </Box>
  );
};

export default TripChecklist;
